import { useEffect, useRef } from 'react'

const revealedSections = new Set<string>()

export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  sectionId: string,
  locale: string,
  options: { threshold?: number; rootMargin?: string } = {}
) {
  const { threshold = 0.1, rootMargin = '0px 0px -40px 0px' } = options
  const ref = useRef<T>(null)

  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      const container = ref.current
      if (!container) return

      const revealAll = () => {
        if (Array.from(container.classList).some(c => c.startsWith('reveal-'))) {
          container.classList.add('revealed')
        }
        container.querySelectorAll('[class*="reveal-"]').forEach(el =>
          el.classList.add('revealed')
        )
        revealedSections.add(sectionId)
      }

      if (revealedSections.has(sectionId)) { revealAll(); return }

      const rect = container.getBoundingClientRect()
      if (rect.top < window.innerHeight * (1 - threshold)) { revealAll(); return }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) { revealAll(); observer.disconnect() }
          })
        },
        { threshold, rootMargin }
      )
      observer.observe(container)
      requestAnimationFrame(() => window.dispatchEvent(new Event('scroll')))
      return () => observer.disconnect()
    })
    return () => cancelAnimationFrame(rafId)
  }, [sectionId, locale, threshold, rootMargin])

  return ref
}
